<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useFormatters } from '@lib/composables/useFormatters'
import BBadge from '@lib/components/base/BBadge.vue'
import { ShieldCheck, ClipboardPlus } from 'lucide-vue-next'
import type { TicketCardFragment } from '@/graphql/generated/sdk'

defineProps<{
    ticket: TicketCardFragment
    selected: boolean
    expanded: boolean
}>()

defineEmits<{
    (e: 'toggle-select'): void
    (e: 'toggle-expand'): void
    (e: 'add-to-clipboard'): void
}>()

const { t } = useI18n()
const fmt = useFormatters()

// Mirror the helpers from TicketsView (kept in sync — see TicketsView.vue
// L110-116): used=true ⇒ greyed "arch" badge, used=false ⇒ green "ok".
function getUsedVariant(used?: boolean | null): 'ok' | 'arch' {
    return used ? 'arch' : 'ok'
}
function getUsedLabel(used?: boolean | null): string {
    return t(used ? 'tickets.usedLabel.used' : 'tickets.usedLabel.unused')
}

function buyerName(t: TicketCardFragment): string {
    return [t.firstName, t.lastName].filter(Boolean).join(' ')
}
</script>

<template>
    <article
        class="bg-white border border-gray-200 rounded-12 p-3 flex flex-col gap-2 transition"
        :class="{ 'ring-2 ring-teal': selected, 'border-teal': expanded }"
    >
        <div class="flex items-start gap-2">
            <!--
                Checkbox handles selection. .stop on click prevents the
                surrounding card's expand-toggle from firing.
            -->
            <input
                type="checkbox"
                :checked="selected"
                class="mt-1 w-5 h-5 accent-teal cursor-pointer shrink-0"
                @click.stop="$emit('toggle-select')"
            />
            <button
                type="button"
                class="flex-1 min-w-0 text-left"
                @click="$emit('toggle-expand')"
            >
                <div class="flex items-center gap-2 flex-wrap">
                    <span class="font-mono text-xs font-semibold text-ink truncate">{{ ticket.code }}</span>
                    <BBadge :variant="getUsedVariant(ticket.used)" :label="getUsedLabel(ticket.used)" />
                </div>
                <div class="mt-1 text-sm">
                    <span v-if="buyerName(ticket)" class="text-ink">{{ buyerName(ticket) }}</span>
                    <span v-else class="text-gray-300">—</span>
                </div>
                <div v-if="ticket.email" class="text-xs text-gray-500 truncate">{{ ticket.email }}</div>
                <div class="mt-1 text-xs text-gray-500">
                    <span>{{ ticket.ticketset?.event?.name ?? '—' }}</span>
                    <span v-if="ticket.ticketset?.name" class="text-gray-400"> · {{ ticket.ticketset.name }}</span>
                </div>
                <div class="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-gray-500 tabular-nums">
                    <div>
                        <span class="text-gray-400">{{ t('tickets.list.columns.boughtAt') }}: </span>
                        <span v-if="ticket.boughtAt">{{ fmt.dateTime(ticket.boughtAt) }}</span>
                        <span v-else class="text-gray-300">—</span>
                    </div>
                    <div>
                        <span class="text-gray-400">{{ t('tickets.list.columns.usedAt') }}: </span>
                        <span v-if="ticket.usedAt">{{ fmt.dateTime(ticket.usedAt) }}</span>
                        <span v-else class="text-gray-300">—</span>
                    </div>
                </div>
            </button>
            <button
                type="button"
                class="shrink-0 w-7 h-7 rounded-6 flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-teal"
                :title="t('tickets.actions.addToClipboard')"
                :aria-label="t('tickets.actions.addToClipboard')"
                @click.stop="$emit('add-to-clipboard')"
            >
                <ClipboardPlus class="w-4 h-4" />
            </button>
            <RouterLink
                :to="{ path: '/tickets/verify', query: { code: ticket.code } }"
                class="shrink-0 w-7 h-7 rounded-6 flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-teal"
                :title="t('tickets.verify.verify')"
                :aria-label="t('tickets.verify.verify')"
                @click.stop
            >
                <ShieldCheck class="w-4 h-4" />
            </RouterLink>
        </div>
    </article>
</template>
